import { FC, useEffect, useMemo, Fragment, useRef, RefObject, memo, useContext } from 'react';
import { BasicAlert, CollapsiblePanel, FormSubmitButton, ICollapsiblePanelComponentProps, IHasChildren } from '@ngt/forms';
import { Box, Button, Chip, Divider, Grid, List, Paper, styled, Tooltip, Typography, ListItemButton, ListItemText, alpha, useScrollTrigger, useTheme, useMediaQuery } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeBranch, faRepeat, faTableList } from '@fortawesome/pro-duotone-svg-icons';
import { ISourceDataFormLayoutComponents, ISourceDataFormSettings, ISourceDataVerificationEvent, ISourceDataVerificationField, ISourceDataVerificationForm, ISourceDataVerificationSection, SourceDataType } from '@/utilities/forms/SourceDataFormProcessor';
import { Virtuoso, VirtuosoHandle,  } from 'react-virtuoso';
import { ISourceDataFormLayoutComponentProps, ISourceDataVerificationItem } from '../../../../utilities/forms/SourceDataFormProcessor';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import useSourceDataEventReviewCount from '@/hooks/form/useSourceDataEventReviewCount';
import useSourceDataFormReviewCount from '@/hooks/form/useSourceDataFormReviewCount';
import useSourceDataReviewCounts from '@/hooks/form/useSourceDataReviewCounts';
import SourceDataReviewCountsContext from '@/contexts/forms/SourceDataReviewCountsContext';
import { ITrial, IPatient, IInstitution, ISourceDataFinding, SourceDataPriority } from '@/api/dtos';
import { MonitoringVisitStatus } from '../../../../api/dtos';
import { faExclamationTriangle, faTriangle } from '@fortawesome/pro-solid-svg-icons';
import { isRestricted } from '@/restricted';

const GridTableOfContentsColumn = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        order: 2
    }
}));

const PriorityContainer = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(2)
}));

const ExtraContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center'
}));

const LabelContainer = styled('div')({
    display: 'flex'
});

const EventRepeatChip = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(2)
}));

const StickyTrigger = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: '-1px',  /* ➜ the trick */

    paddingTop: 3,
    color: 'pink',
    height: 1,
    overflow: 'hidden'
}));

const StickyBox = styled(Box)(({ hasBreadcrumbs, theme }: any) => ({
    [theme.breakpoints.up('lg')]: {
        position: 'sticky',
        top: 0,
    
        '&.is-pinned2': {
            paddingTop: 24,

            '& > div': {
                maxHeight: 'calc(100vh - (48px + 24px + 24px))',
        
                '@media (min-width: 600px)': {
                    maxHeight: 'calc(100vh - (64px + 24px + 24px))'
                }
            }
        },
    
        '&.is-pinned': {
            paddingTop: (!isRestricted ? 48 : 0) /* header height */ + 24,
        
            '@media (min-width: 600px)': {
                paddingTop: (!isRestricted ? 64 : 0) /* header height */ + 24,
            },
            
            '& > div': {
                maxHeight: !isRestricted ? 'calc(100vh - (48px + 24px + 24px))' : 'calc(100vh - (24px + 24px))',
        
                '@media (min-width: 600px)': {
                    maxHeight: !isRestricted ? 'calc(100vh - (64px + 24px + 24px))' : 'calc(100vh - (24px + 24px))',
                }
            }
        },
    
    
        '& > div': {
            maxHeight: !isRestricted ?
                hasBreadcrumbs ? 'calc(100vh - (48px + 24px + 24px + 122px))' : 'calc(100vh - (48px + 24px + 24px + 72px))' :
                hasBreadcrumbs ? 'calc(100vh - (24px + 24px + 122px))' : 'calc(100vh - (24px + 24px + 72px))',
    
            '@media (min-width: 600px)': {
                maxHeight: !isRestricted ?
                    hasBreadcrumbs ? 'calc(100vh - (64px + 24px + 24px + 122px))' : 'calc(100vh - (64px + 24px + 24px + 72px))' :
                    hasBreadcrumbs ? 'calc(100vh - (24px + 24px + 122px))' : 'calc(100vh - (24px + 24px + 72px))',
                //top: 64 /* header height */ + 24,
            },
        }
    }
}));

const SideNavigationEventItem = styled(Box)(({theme}) => ({
    marginTop: -1,
    background: theme.palette.background.paper,
    backgroundImage: theme.palette.mode === 'dark' ? 
        `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(theme.palette.common.white, 0.05)})` :
        undefined,
    position: 'sticky',
    top: 0,
    zIndex: 1000
}));

const SideNavigationFormItem = styled(Box)(({theme}) => ({
    marginTop: -1,
    background: theme.palette.background.paper,
    backgroundImage: theme.palette.mode === 'dark' ? 
        `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(theme.palette.common.white, 0.05)})` :
        undefined,
    borderLeft: theme.palette.mode === 'dark' ? 
            `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
            `solid 1px ${alpha(theme.palette.common.black, 0.12)}` ,
    marginLeft: theme.spacing(4),
    position: 'relative'
}));

const StickyTableOfContents = styled(Box)(({ theme }) => ({
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh'
}));

const CompletableListItemButton = styled(ListItemButton)(({ complete, theme }: any) => ({
    //background: `linear-gradient(to right, ${alpha(theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.light)} ${complete}%, transparent ${complete}%);`
    background: `linear-gradient(to right, ${theme.palette.mode === 'dark' ? alpha(theme.palette.success.dark, 0.5) : alpha(theme.palette.success.light, 0.5)} ${complete}%, transparent ${complete}%);`
}));

const CompletableBox = styled(Box)(({ complete, placement, theme }: any) => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    //background: `linear-gradient(to right, ${alpha(theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.light)} ${complete}%, transparent ${complete}%);`
    background: `linear-gradient(to right, ${theme.palette.mode === 'dark' ? alpha(theme.palette.success.dark, 0.3) : alpha(theme.palette.success.light, 0.3)} ${complete}%, transparent ${complete}%);`
}));

// Item contents are cached properly with React.memo
const InnerItem = memo(({ index, item, context}: {index: number; item: ISourceDataVerificationItem; context: IVirtuosoContext}) => {
    
    if (item.type === SourceDataType.Event) {
        return ( 
            <context.Layout.Event {...item} {...context} />
        )
    }

    if (item.type === SourceDataType.Form) {
        return ( 
            <context.Layout.Form {...item}  {...context} />
        )
    }

    if (item.type === SourceDataType.Section) {
        if (item.formSectionMetadata?.repeating) {
            return ( 
                <context.Layout.Collection {...item}  {...context} />
            )
        }

        return ( 
            <context.Layout.Group {...item}  {...context} />
        )
    }

    if (item.type === SourceDataType.Field) {
        return ( 
            <context.Layout.Field {...item}  {...context} />
        )
    }

    return (
        <>
            Unknown Item
        </>
    )
})
  
interface IVirtuosoContext { 
    Layout: ISourceDataFormLayoutComponents; 
    settings?: ISourceDataFormSettings; 
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
}

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: ISourceDataVerificationItem, context: IVirtuosoContext) => {
    return <InnerItem index={index} item={item} context={context} />
}

interface ISideNavExtraProps {
    index: number; 
    settings?: ISourceDataFormSettings; 
    virtuoso: RefObject<VirtuosoHandle>; 
    readOnly?: boolean;
}

const SideNavigationEvent: FC<ISourceDataVerificationEvent & ISideNavExtraProps> = ({
    virtuoso,
    index,
    settings,
    readOnly,
    ...event
}) => {
    const {
        eventLabel,
        eventMetadata,
        eventRepeat,
        eventPropertyPath,
        eventConfiguration,
        eventIdentifier,
        eventSourceData
    } = event;

    const counts = useSourceDataEventReviewCount(eventPropertyPath!, event)

    const complete = useMemo(() => {

        const c = {
            reviewed: 0,
            verified: 0
        }

        if (!readOnly && settings?.showSideNavigationReviewProgress && counts?.reviewed && counts?.totalToReview) {
            c.reviewed = (counts.reviewed / counts.totalToReview) * 100;
        }

        if (!readOnly && settings?.showSideNavigationVerificationProgress && counts?.verified && counts?.totalToVerify) {
            c.verified = (counts.verified / counts.totalToVerify) * 100;
        }
        
        return c;
    }, [counts?.totalToReview, counts?.reviewed, counts?.verified, counts?.totalToVerify, settings?.showSideNavigationReviewProgress, settings?.showSideNavigationVerificationProgress, readOnly]);

    return (
        <SideNavigationEventItem>
            <Divider />
            <CompletableBox 
                //@ts-ignore-next-line
                complete={complete.reviewed}
            />
            <CompletableBox 
                //@ts-ignore-next-line
                complete={complete.verified}
            />
            <CompletableListItemButton
                onClick={() => { 
                    virtuoso.current?.scrollToIndex({
                        index: index,
                        align: 'start',
                        behavior: 'auto',
                        offset: -64
                    })
                }}
            >
                <ListItemText
                    primary={
                        <LabelContainer>
                            {eventLabel}
                            <ExtraContainer>
                                {
                                    eventMetadata?.repeating && eventRepeat && (
                                        <Tooltip title="Repeat">
                                            <EventRepeatChip 
                                                label={eventRepeat} 
                                                icon={<FontAwesomeIcon icon={faRepeat} />}
                                                size="small"
                                            />
                                        </Tooltip>
                                    )
                                }
                                {
                                    eventSourceData?.priority === SourceDataPriority.Low && (
                                        <Tooltip title="Priority: Low">
                                            <PriorityContainer sx={{color: 'success.main', transform: 'rotate(180deg)'}}>
                                                <FontAwesomeIcon icon={faTriangle} />
                                            </PriorityContainer>
                                        </Tooltip>
                                    )
                                }
                                {
                                    eventSourceData?.priority === SourceDataPriority.High && (
                                        <Tooltip title="Priority: High">
                                            <PriorityContainer sx={{color: 'warning.main'}}>
                                                <FontAwesomeIcon icon={faTriangle} />
                                            </PriorityContainer>
                                        </Tooltip>
                                    )
                                }
                                {
                                    eventSourceData?.priority === SourceDataPriority.Critical && (
                                        <Tooltip title="Priority: Critical">
                                            <PriorityContainer sx={{color: 'error.main'}}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                            </PriorityContainer>
                                        </Tooltip>
                                    )
                                }
                            </ExtraContainer>
                        </LabelContainer>
                    }
                />
            </CompletableListItemButton>
            <Divider />
        </SideNavigationEventItem>
    )
}

const SideNavigationForm: FC<ISourceDataVerificationForm & ISideNavExtraProps> = ({
    virtuoso,
    index,
    settings,
    readOnly,
    ...form
}) => {
    const {
        formLabel,
        formVersionLabel,
        formVersionEnabled,
        formConfiguration,
        formSourceData,
        formPropertyPath,
        formIdentifier
    } = form;

    const counts = useSourceDataFormReviewCount(formPropertyPath!, form);

    const complete = useMemo(() => {

        const c = {
            reviewed: 0,
            verified: 0
        }

        if (!readOnly && settings?.showSideNavigationReviewProgress && counts?.reviewed && counts?.totalToReview) {
            c.reviewed = (counts.reviewed / counts.totalToReview) * 100;
        }

        if (!readOnly && settings?.showSideNavigationVerificationProgress && counts?.verified && counts?.totalToVerify) {
            c.verified = (counts.verified / counts.totalToVerify) * 100;
        }
        
        return c;
    }, [counts?.totalToReview, counts?.reviewed, counts?.verified, counts?.totalToVerify, settings?.showSideNavigationReviewProgress, settings?.showSideNavigationVerificationProgress, readOnly]);

    return (
        <SideNavigationFormItem>
            <Divider />
            <CompletableBox 
                //@ts-ignore-next-line
                complete={complete.reviewed}
            />
            <CompletableBox 
                //@ts-ignore-next-line
                complete={complete.verified}
            />
            <CompletableListItemButton
                onClick={() => { 
                    virtuoso.current?.scrollToIndex({
                        index: index,
                        align: 'start',
                        behavior: 'auto',
                        offset: -64
                    })
                }}
            >
                <ListItemText
                    primary={
                        <LabelContainer>
                            {formLabel}
                            <ExtraContainer>
                                {
                                    formSourceData?.priority === SourceDataPriority.Low && (
                                        <Tooltip title="Priority: Low">
                                            <PriorityContainer sx={{color: 'success.main', transform: 'rotate(180deg)'}}>
                                                <FontAwesomeIcon icon={faTriangle} />
                                            </PriorityContainer>
                                        </Tooltip>
                                    )
                                }
                                {
                                    formSourceData?.priority === SourceDataPriority.High && (
                                        <Tooltip title="Priority: High">
                                            <PriorityContainer sx={{color: 'warning.main'}}>
                                                <FontAwesomeIcon icon={faTriangle} />
                                            </PriorityContainer>
                                        </Tooltip>
                                    )
                                }
                                {
                                    formSourceData?.priority === SourceDataPriority.Critical && (
                                        <Tooltip title="Priority: Critical">
                                            <PriorityContainer sx={{color: 'error.main'}}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                            </PriorityContainer>
                                        </Tooltip>
                                    )
                                }
                            </ExtraContainer>
                        </LabelContainer>
                    }
                />
            </CompletableListItemButton>
            <Divider />
        </SideNavigationFormItem>
    )
}


const SourceDataFormLayoutTitled: FC<ISourceDataFormLayoutComponentProps> = ({
    events,
    settings,
    children,
    list,
    Layout,
    readOnly,
    institution,
    patient,
    trial,
    hideVerification,
    hasBreadcrumbs,
    sourceData
}) => {
    const virtuoso = useRef<VirtuosoHandle>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    
    const eventList = useMemo(() => {
        if (!settings?.showSideNavigation) {
            return [];
        }

        return list
            .map((item, i, a) => {
                if (item.type !== SourceDataType.Event && item.type !== SourceDataType.Form) {
                    return null;
                }



                if (item.type === SourceDataType.Event) {
                    if (!item.eventSourceData && settings?.hideEvents) {
                        return null
                    }

                    return (
                        <SideNavigationEvent key={item.eventIdentifier} 
                            {...item}
                            settings={settings}
                            virtuoso={virtuoso}
                            readOnly={readOnly}
                            index={i}
                        />
                    )   
                }

                if (!item.formSourceData && settings?.hideForms) {
                    return null;
                }

                if (!item.formSourceData && !item.formVersionEnabled && settings?.hideVersions) {
                    return null;
                }
                
                if (!settings?.showSideNavigationForms) {
                    return null;
                }

                return (
                    <SideNavigationForm key={item.formIdentifier} 
                        {...item}
                        settings={settings}
                        virtuoso={virtuoso}
                        readOnly={readOnly}
                        index={i}
                    />
                )   
            })
            .filter(item => !!item)
    }, [list, settings, virtuoso, readOnly])
    
    const trigger2 = useScrollTrigger({
        disableHysteresis: true,
        threshold: hasBreadcrumbs ? 140 : 90
    })

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: hasBreadcrumbs ? 150 : 100
    })

    return (
        <Grid
            container 
            spacing={2}
        >  
            <GridTableOfContentsColumn 
                item
                xs={12}
                sm={12}
                md={12}
                lg={4}
                xl={3}
            >
                <StickyBox 
                    //@ts-ignore-next-line
                    hasBreadcrumbs={hasBreadcrumbs ? '1' : undefined}
                    className={(trigger ? 'is-pinned' : '') + (trigger2 ? ' is-pinned2' : '')}
                >
                    <StickyTableOfContents>
                        {
                            !!settings?.showSideNavigation && (
                                <Paper
                                    sx={{
                                        overflow: 'auto',
                                        flex: '1 1 auto',
                                        height: '100vh'
                                    }}
                                >
                                    <List 
                                        disablePadding 
                                    >
                                        {eventList}
                                    </List>
                                </Paper>
                            )
                        }
                        {
                            !isMobile && !readOnly && (
                                <Box sx={{mt: 'auto', textAlign: 'right'}}>
                                    <FormSubmitButton
                                        variant="contained"
                                        fullWidth
                                    >
                                        Finish Patient Review
                                    </FormSubmitButton>
                                </Box>
                            )
                        }
                    </StickyTableOfContents>
                </StickyBox>
            </GridTableOfContentsColumn>
            <Grid 
                item
                xs={12}
                sm={12}
                md={12}
                lg={8}
                xl={9}
            >
                { 
                    (!events || events.length === 0) && (
                        <BasicAlert
                            title="No Events Configured"
                            description="No events have been included in this configuration."
                            severity="info"
                            icon={faInfoCircle}
                        />
                    )
                }
                { 
                    list && list.length !== 0 && (
                        <>
                            <Virtuoso
                                context={{ Layout, settings, events, list, institution, trial, patient, readOnly, hideVerification }}
                                increaseViewportBy={400}
                                ref={virtuoso}
                                useWindowScroll
                                data={list}
                                itemContent={virtuosoItemContent}
                            />
                            {
                                isMobile && !readOnly && (
                                    <Box sx={{mt: 2, textAlign: 'right'}}>
                                        <FormSubmitButton
                                            variant="contained"
                                        >
                                            Mark Review Finished
                                        </FormSubmitButton>
                                    </Box>
                                )
                            }
                        </>
                    )
                }
            </Grid>
        </Grid>
    );
}

export default SourceDataFormLayoutTitled;